import { BordOneToneIcon } from 'components/NudosComponents';
import { MENTION_STATUS, MENTION_TYPES, IGroupedMentionWithMentioner, IGroupedMention } from 'types/dashboard.types';

export const MENTIONS_COLUMNS_CONTENT = {
  [MENTION_STATUS.PENDING_VIEWING]: {
    columnTitle: 'Pendientes de visualización',
    noMentions: {
      message: 'No tienes menciones pendientes por ver',
      icon: <BordOneToneIcon variant="atSign" />
    }
  },
  [MENTION_STATUS.PENDING_RESOLUTION]: {
    columnTitle: 'Pendientes de resolución',
    noMentions: {
      message: 'No tienes menciones vistas pendientes por resolver',
      icon: <BordOneToneIcon variant="eye" />
    }
  },
  [MENTION_STATUS.CLOSED]: {
    columnTitle: 'Resueltos',
    noMentions: {
      message: 'No tienes menciones resueltas en los últimos 7 días',
      icon: <BordOneToneIcon variant="checkCircle" />
    }
  }
};

export const ACCORDION_CARD_CONTENT = {
  [MENTION_TYPES.ORDER]: {
    title: 'Orden N°',
    icon: <BordOneToneIcon variant="menu" stroke="white" customWidth="12px" />
  },
  [MENTION_TYPES.LOGISTIC_SERVICE]: {
    title: 'Servicio Logistico N°',
    icon: <BordOneToneIcon variant="truck" stroke="white" customWidth="12px" />
  },
  [MENTION_TYPES.STOCK_ORDER]: {
    title: 'Orden Stock N°',
    icon: <BordOneToneIcon variant="shop" stroke="white" customWidth="12px" />
  }
};

export interface IMentionToggleAction {
  alertText: string;
  updateObject: { wasSeen: boolean; wasResolved: boolean };
}

export interface IMentionBulkActions {
  [key: string]: {
    toggleSeen: IMentionToggleAction;
    toggleResolved: IMentionToggleAction;
  };
}

export interface IMentionStates {
  [key: string]: {
    seen: boolean;
    resolved: boolean;
  };
}

export const MENTION_CURRENT_STATES: IMentionStates = {
  [MENTION_STATUS.PENDING_VIEWING]: {
    seen: false,
    resolved: false
  },
  [MENTION_STATUS.PENDING_RESOLUTION]: {
    seen: true,
    resolved: false
  },
  [MENTION_STATUS.CLOSED]: {
    seen: true,
    resolved: true
  }
};

export const MENTION_BULK_ACTIONS: IMentionBulkActions = {
  [MENTION_STATUS.PENDING_VIEWING]: {
    toggleSeen: {
      alertText: `¿Quieres marcar todas las menciones como leídas?\n Podrás revertirlo desmarcando las menciones de forma individual o todas a la vez.`,
      updateObject: { wasSeen: true, wasResolved: false }
    },
    toggleResolved: {
      alertText: `¿Quieres marcar todas las menciones como leídas y resueltas?\n Podrás revertirlo desmarcando las menciones de forma individual o todas a la vez.`,
      updateObject: { wasSeen: true, wasResolved: true }
    }
  },
  [MENTION_STATUS.PENDING_RESOLUTION]: {
    toggleSeen: {
      alertText: `¿Quieres desmarcar todas las menciones como no leídas?\n Podrás revertirlo marcando las menciones de forma individual o todas a la vez.`,
      updateObject: { wasSeen: false, wasResolved: false }
    },
    toggleResolved: {
      alertText: `¿Quieres marcar todas las menciones como resueltas?\n Podrás revertirlo desmarcando las menciones de forma individual o todas a la vez.`,
      updateObject: { wasSeen: true, wasResolved: true }
    }
  },
  [MENTION_STATUS.CLOSED]: {
    toggleSeen: {
      alertText: `¿Quieres desmarcar todas las menciones como no leídas?\n Podrás revertirlo marcando las menciones de forma individual o todas a la vez.`,
      updateObject: { wasSeen: false, wasResolved: false }
    },
    toggleResolved: {
      alertText: `¿Quieres desmarcar todas las menciones como no resueltas?\n Podrás revertirlo marcando las menciones de forma individual o todas a la vez.`,
      updateObject: { wasSeen: true, wasResolved: false }
    }
  }
};

export const MENTIONS_LIST_TITLE = {
  [MENTION_STATUS.PENDING_VIEWING]: {
    firstTitle: 'menciones de hoy',
    secondTitle: 'menciones anteriores'
  },
  [MENTION_STATUS.PENDING_RESOLUTION]: {
    firstTitle: 'menciones de hoy sin resolver',
    secondTitle: 'menciones anteriores sin resolver'
  },
  [MENTION_STATUS.CLOSED]: {
    firstTitle: 'menciones resueltas en los últimos 7 días'
  }
};

/**
 * Construye la URL de redirección correcta según el tipo de mención
 * @param type - Tipo de mención (ORDER, LOGISTIC_SERVICE, STOCK_ORDER)
 * @param sourceId - ID de la fuente (usado para orders y logistic services)
 * @param sourceUuid - UUID de la fuente (usado para stock orders)
 * @returns URL completa para redirección
 */
export const buildRedirectUrl = (
  type?: string | null,
  sourceId?: number | null,
  sourceUuid?: string | null
): string => {
  if (!type || !sourceId) return '';

  const baseUrls = {
    [MENTION_TYPES.ORDER]: '/order/details/',
    [MENTION_TYPES.LOGISTIC_SERVICE]: '/logistic-service/details/',
    [MENTION_TYPES.STOCK_ORDER]: '/stock/details/'
  };

  const baseUrl = baseUrls[type as keyof typeof baseUrls];

  if (!baseUrl) {
    return '';
  }

  // Para stock orders, usar sourceUuid si está disponible, sino sourceId
  if (type === MENTION_TYPES.STOCK_ORDER) {
    return `${baseUrl}${sourceUuid || sourceId}`;
  }

  // Para otros tipos, usar sourceId
  return `${baseUrl}${sourceId}`;
};

export const redirectToConversation = (redirectUrl?: string | null) => {
  if (!redirectUrl) return;

  if (redirectUrl) {
    window.open(redirectUrl, '_blank');
  }
};

/**
 * Verifica si una mención es de hoy comparando las fechas
 * @param mention - La mención a verificar
 * @returns true si la mención es de hoy, false en caso contrario
 */
const isTodayMention = (mention: IGroupedMentionWithMentioner): boolean => {
  const today = new Date();
  const mentionDate = new Date(mention.createdAt);

  return (
    mentionDate.getDate() === today.getDate() &&
    mentionDate.getMonth() === today.getMonth() &&
    mentionDate.getFullYear() === today.getFullYear()
  );
};

interface IDivideMentionsByDateResponse {
  todayMentions: IGroupedMentionWithMentioner[];
  previousMentions: IGroupedMentionWithMentioner[];
}

/**
 * Divide las menciones agrupadas en dos categorías: de hoy y anteriores
 * @param groupedMention - Array de menciones agrupadas
 * @returns Objeto con menciones de hoy y anteriores
 */
export const divideMentionsByDate = (groupedMention?: IGroupedMention): IDivideMentionsByDateResponse => {
  const todayMentions: IGroupedMentionWithMentioner[] = [];
  const previousMentions: IGroupedMentionWithMentioner[] = [];

  if (!groupedMention) return { todayMentions, previousMentions };

  const todayMentionsInGroup = groupedMention.mentions.filter(isTodayMention);
  const previousMentionsInGroup = groupedMention.mentions.filter(mention => !isTodayMention(mention));

  if (todayMentionsInGroup.length > 0) {
    todayMentions.push(...todayMentionsInGroup);
  }

  if (previousMentionsInGroup.length > 0) {
    previousMentions.push(...previousMentionsInGroup);
  }

  return { todayMentions, previousMentions };
};

/**
 * Obtiene los IDs de todas las menciones agrupadas
 * @param groupedMention - Array de menciones agrupadas
 * @returns Array de IDs de las menciones
 */
export const getAllMentionIds = (groupedMention?: IGroupedMention): number[] => {
  if (!groupedMention) return [];

  return groupedMention.mentions.map(mention => mention.id);
};
